import { z } from 'zod';
import { mongoObjectIdString, optionalMongoObjectId } from './object-id-helpers.js';

const catalogId = mongoObjectIdString;

const pdInventoryCatalogFields = {
    personalData: z.string().optional(),
    personalDataCategoryIds: z.array(catalogId).optional(),
    personalDataCategoryId: optionalMongoObjectId,
    processingPurpose: z.string().optional(),
    processingPurposeId: optionalMongoObjectId,
    subjectPersonGroup: z.string().optional(),
    subjectPersonGroupId: optionalMongoObjectId,
    purchaserGroup: z.string().optional(),
    recipientGroup: z.string().optional(),
    recipientGroupId: optionalMongoObjectId,
    /** @deprecated Prefer recipientGroupId */
    buyerGroupId: optionalMongoObjectId,
    collectionMethod: z.string().optional(),
    collectionMethodId: optionalMongoObjectId,
    legalCauses: z.string().optional(),
    legalReasonId: optionalMongoObjectId,
};

function applyPdCatalogRefine(schema) {
    return schema.superRefine((data, ctx) => {
        const categoryIdsPresent = Object.prototype.hasOwnProperty.call(data, 'personalDataCategoryIds');
        const categoryIdPresent = Object.prototype.hasOwnProperty.call(data, 'personalDataCategoryId');
        const personalDataPresent = Object.prototype.hasOwnProperty.call(data, 'personalData');
        if (categoryIdsPresent || categoryIdPresent || personalDataPresent) {
            const ids = Array.isArray(data.personalDataCategoryIds)
                ? data.personalDataCategoryIds.filter(Boolean)
                : [];
            const single = data.personalDataCategoryId || null;
            if (!ids.length && !single && !data.personalData) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['personalDataCategoryIds'],
                    message: 'personalDataCategoryIds is required',
                });
            }
        }

        const pairs = [
            ['processingPurposeId', 'processingPurpose'],
            ['subjectPersonGroupId', 'subjectPersonGroup'],
            ['recipientGroupId', 'purchaserGroup'],
            ['collectionMethodId', 'collectionMethod'],
            ['legalReasonId', 'legalCauses'],
        ];
        for (const [idKey, legacyKey] of pairs) {
            const idPresent = Object.prototype.hasOwnProperty.call(data, idKey);
            const legacyPresent = Object.prototype.hasOwnProperty.call(data, legacyKey);
            if (!idPresent && !legacyPresent) continue;
            if (!data[idKey] && !data[legacyKey]) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [idKey], message: `${idKey} is required` });
            }
        }
    });
}

export const PersonalDataInventorySchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    activity: z.string().min(1, 'Activity is required'),
    activityCode: z.string().optional(),
    unit: optionalMongoObjectId,
    personalDataSource: z.string().optional(),
    personalDataLocation: z.string().optional(),
    ...pdInventoryCatalogFields,
    partyInstanceId: optionalMongoObjectId,
    storageEnvironment: z.string().optional(),
    retentionStartDate: z.preprocess((arg) => {
        if (typeof arg === 'string' || arg instanceof Date) return new Date(arg);
    }, z.date()).optional(),
    /** RetentionPeriod catalog FK — duration SoT for PDI / scanner (GRC-1167). */
    retentionPeriodId: optionalMongoObjectId,
    /** Display label denormalized from RetentionPeriod.name — not duration SoT. */
    retentionDuration: z.string().optional(),
    retentionReason: z.string().optional(),
    internationalTransfer: z.union([z.boolean(), z.string()]).optional(),
    internationalTransferIds: z.array(optionalMongoObjectId).optional(),
    transferCountries: z.array(z.string()).optional(),
    transferSafeguards: z.string().optional(),
    securityMeasures: z.array(optionalMongoObjectId).optional(),
    explicitConsentObligation: z.union([z.boolean(), z.string()]).default(false),
    disclosureObligation: z.union([z.boolean(), z.string()]).default(false),
    degreeOfConfidentiality: z.string().optional(),
    confidentialityValue: z.string().optional(),
    integrityValue: z.string().optional(),
    availabilityValue: z.string().optional(),
    description: z.string().optional(),
    relatedAssets: z.array(optionalMongoObjectId).optional(),
    status: z.enum(['active', 'inactive', 'draft']).optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
    customFields: z.record(z.unknown()).optional(),
});

export const PdInventoryFormObjectSchema = PersonalDataInventorySchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    personalDataCategoryIds: z.array(catalogId).min(1),
    personalDataCategoryId: optionalMongoObjectId,
    processingPurposeId: optionalMongoObjectId,
    subjectPersonGroupId: optionalMongoObjectId,
    recipientGroupId: optionalMongoObjectId,
    collectionMethodId: optionalMongoObjectId,
    legalReasonId: optionalMongoObjectId,
});

export const CreatePersonalDataInventorySchema = applyPdCatalogRefine(PdInventoryFormObjectSchema);

export const UpdatePersonalDataInventorySchema = applyPdCatalogRefine(PersonalDataInventorySchema.partial());
export const PdInventoryFormSchema = PdInventoryFormObjectSchema;
export const PersonalDataInventoryFormSchema = PdInventoryFormSchema;
